import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Textarea } from "@chakra-ui/react";
import { normalizeSecretKey } from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { useAsyncActionHandler } from "@umami/state";
import { FormProvider } from "react-hook-form";

import { usePasskey } from "../../../hooks/usePasskey";
import { trackButtonClick } from "../../../utils/analytics";
import { SetupPassword } from "../SetupPassword";

type FormValues = { secretKey: string; secretKeyPassword: string };

export const SecretKeyTab = () => {
  const { createSecretKeyWallet, isPasskeyFlow, isPasskeyUser } = usePasskey();
  const { handleAsyncAction, isLoading } = useAsyncActionHandler();
  const form = useMultiForm<FormValues>({
    mode: "onBlur",
    defaultValues: { secretKey: "", secretKeyPassword: "" },
  });
  const { openWith, onClose } = useDynamicModalContext();

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = form;

  const onSubmit = ({ secretKey }: FormValues) =>
    handleAsyncAction(async () => {
      // validate secret key
      await normalizeSecretKey(secretKey);

      if (isPasskeyFlow && isPasskeyUser) {
        await createSecretKeyWallet(secretKey);
        onClose();
      } else {
        trackButtonClick("continue_with_secret", {
          button_label: "Next",
          section: "Welcome",
          flow: "Onboarding",
          action: "button_click",
        });

        return openWith(<SetupPassword mode="secret_key" />);
      }
    });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection="column" gap="24px">
          <FormControl isInvalid={!!form.formState.errors.secretKey}>
            <FormLabel>Secret key</FormLabel>
            <Textarea
              minHeight="120px"
              fontSize="18px"
              {...register("secretKey", {
                required: "Secret key is required",
              })}
              _focusVisible={{
                outline: "none",
                borderColor: "gray.400",
                boxShadow: "none",
              }}
              placeholder="Type here..."
            />
            {errors.secretKey && <FormErrorMessage>{errors.secretKey.message}</FormErrorMessage>}
          </FormControl>

          <Button
            width="full"
            isDisabled={!isValid}
            isLoading={isLoading}
            type="submit"
            variant="primary"
          >
            Next
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
